import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from "recharts";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { Activity, Calendar, TrendingDown, Repeat, AlertTriangle, Clock, Stethoscope, Award, Loader2, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { adminUrl } from "@/lib/adminPath";

/**
 * Admin Dashboard — Engagement Analytics section (Phase F).
 *
 * Fetches /api/admin/analytics/engagement once and renders 7 widgets:
 *   • KPI strip (Visits Today, This Month, Repeat Rate, Total Savings)
 *   • Monthly visit trend (12-mo line chart)
 *   • Top Departments (bar chart)
 *   • Top Doctors (list)
 *   • Top Benefits Used (list)
 *   • Members Near Expiry (next 30 days list)
 *   • Low Engagement Members (At Risk + Inactive)
 */
export default function EngagementAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/admin/analytics/engagement")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mt-12 rounded-2xl border border-[#E1E8F0] bg-white p-12 text-center text-[#5C6B7A]" data-testid="dashboard-engagement-loading">
        <Loader2 className="h-6 w-6 animate-spin inline" />
      </div>
    );
  }
  if (!data) {
    return null; // analytics unavailable (likely permission) — silently hide
  }

  const trendData = (data.monthly_visit_trend || []).map((m) => ({
    ...m,
    label: m.month?.slice(2), // YY-MM
  }));

  return (
    <div className="mt-12 space-y-6" data-testid="dashboard-engagement">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="font-display text-2xl font-bold text-[#0F2540] flex items-center gap-2">
          <Activity className="h-6 w-6 text-[#1E4F87]" />
          Engagement Analytics
        </h2>
        <button onClick={() => nav(adminUrl("/visits"))} className="inline-flex items-center gap-1.5 text-sm text-[#1E4F87] font-semibold hover:underline">
          Open visits page <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-engagement-kpis">
        <Kpi label="Visits today" value={data.visits_today} icon={Calendar} testid="kpi-visits-today" />
        <Kpi label="Visits this month" value={data.visits_this_month} icon={Activity} testid="kpi-visits-month" />
        <Kpi label="Repeat visit rate" value={`${data.repeat_visit_rate}%`} sub={`${data.repeat_visitors_12mo} of ${data.total_visitors_12mo} members`} icon={Repeat} tone="navy" testid="kpi-repeat-rate" />
        <Kpi label="Total savings" value={inr(data.total_savings_generated)} sub="lifetime" icon={TrendingDown} tone="gold" testid="kpi-total-savings" />
      </div>

      {/* Trend + top departments side by side */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[#E1E8F0] bg-white p-5" data-testid="dashboard-trend-chart">
          <h3 className="font-display text-lg font-bold text-[#0F2540]">Monthly visit trend</h3>
          <p className="text-xs text-[#5C6B7A]">Visits and savings over the last 12 months</p>
          <div className="h-64 mt-3">
            {trendData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-[#5C6B7A]">No visits logged in the last 12 months.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E1E8F0" />
                  <XAxis dataKey="label" stroke="#5C6B7A" fontSize={11} />
                  <YAxis yAxisId="left" stroke="#5C6B7A" fontSize={11} />
                  <YAxis yAxisId="right" orientation="right" stroke="#C89B3C" fontSize={11} tickFormatter={(v) => `₹${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                  <Tooltip formatter={(v, n) => n === "savings" ? inr(v) : v} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line yAxisId="left" type="monotone" dataKey="visits" name="Visits" stroke="#1E4F87" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line yAxisId="right" type="monotone" dataKey="savings" name="Savings" stroke="#C89B3C" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-[#E1E8F0] bg-white p-5" data-testid="dashboard-top-departments">
          <h3 className="font-display text-lg font-bold text-[#0F2540]">Top Departments</h3>
          <p className="text-xs text-[#5C6B7A]">By number of visits (lifetime)</p>
          <div className="h-64 mt-3">
            {(data.top_departments || []).length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-[#5C6B7A]">No visits logged yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_departments} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E1E8F0" />
                  <XAxis type="number" stroke="#5C6B7A" fontSize={11} />
                  <YAxis dataKey="department" type="category" stroke="#5C6B7A" fontSize={11} width={80} />
                  <Tooltip />
                  <Bar dataKey="visit_count" fill="#1E4F87" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Top doctors + Top benefits */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ListCard
          title="Top Doctors"
          icon={Stethoscope}
          empty="No doctors recorded yet."
          rows={data.top_doctors || []}
          render={(d) => ({ left: d.doctor, right: `${d.visit_count} visits` })}
          testid="dashboard-top-doctors"
        />
        <ListCard
          title="Most-Used Benefits"
          icon={Award}
          empty="No benefits used yet."
          rows={data.top_benefits || []}
          render={(b) => ({
            left: b.title,
            sub: b.category,
            right: `${b.usage_count} uses · ${inr(b.savings_generated)} saved`,
          })}
          testid="dashboard-top-benefits"
        />
      </div>

      {/* Retention widgets */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ListCard
          title="Members Near Expiry"
          icon={Clock}
          empty="No memberships expiring in the next 30 days."
          rows={data.members_near_expiry || []}
          tone="gold"
          render={(m) => ({
            left: m.full_name,
            sub: `${m.plan_name} · ${m.membership_number}`,
            right: m.valid_till ? `expires ${new Date(m.valid_till).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}` : "—",
            onClick: () => nav(adminUrl(`/renewals?q=${m.member_id}`)),
          })}
          testid="dashboard-near-expiry"
        />
        <ListCard
          title="Low Engagement Members"
          icon={AlertTriangle}
          empty="No low-engagement members in the active cohort 🎉"
          rows={data.low_engagement_members || []}
          tone="ember"
          render={(m) => ({
            left: m.full_name,
            sub: `${m.plan_name} · ${m.tier_label}`,
            right: m.days_since_last_visit != null ? `${m.days_since_last_visit}d since visit` : "Never visited",
            onClick: () => nav(adminUrl(`/members?q=${m.member_id}`)),
          })}
          testid="dashboard-low-engagement"
        />
      </div>
    </div>
  );
}


function Kpi({ label, value, sub, icon: Icon, tone = "navy", testid }) {
  const toneCls = {
    navy: "bg-[#EAF1FA] text-[#1E4F87]",
    gold: "bg-[#FAF1DC] text-[#8C6A00]",
    emerald: "bg-[#E5F2EA] text-[#1F7A3D]",
  }[tone] || "bg-[#EAF1FA] text-[#1E4F87]";
  return (
    <div className="rounded-2xl border border-[#E1E8F0] bg-white p-4 flex items-center gap-4" data-testid={testid}>
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${toneCls}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-[#5C6B7A]">{label}</div>
        <div className="font-display text-xl font-bold text-[#0F2540]">{value}</div>
        {sub && <div className="text-[10px] text-[#5C6B7A]">{sub}</div>}
      </div>
    </div>
  );
}

function ListCard({ title, icon: Icon, rows, render, empty, testid, tone = "navy" }) {
  const dotCls = {
    navy: "bg-[#EAF1FA] text-[#1E4F87]",
    gold: "bg-[#FAF1DC] text-[#8C6A00]",
    ember: "bg-[#FCE9E6] text-[#A53A2A]",
  }[tone] || "bg-[#EAF1FA] text-[#1E4F87]";
  return (
    <div className="rounded-2xl border border-[#E1E8F0] bg-white p-5" data-testid={testid}>
      <h3 className="font-display text-lg font-bold text-[#0F2540] flex items-center gap-2">
        <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${dotCls}`}><Icon className="h-4 w-4" /></span>
        {title}
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-[#5C6B7A] mt-4">{empty}</p>
      ) : (
        <ul className="mt-3 divide-y divide-[#E1E8F0]">
          {rows.map((r, i) => {
            const row = render(r);
            const Tag = row.onClick ? "button" : "div";
            return (
              <Tag
                key={i}
                onClick={row.onClick}
                className={`w-full py-2.5 flex items-center justify-between gap-3 text-left ${row.onClick ? "hover:bg-[#FAFBFD] px-1 rounded transition-colors cursor-pointer" : ""}`}
                data-testid={`${testid}-row-${i}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#0F2540] truncate">{row.left}</div>
                  {row.sub && <div className="text-xs text-[#5C6B7A] truncate">{row.sub}</div>}
                </div>
                <div className="text-xs text-[#5C6B7A] font-medium whitespace-nowrap">{row.right}</div>
              </Tag>
            );
          })}
        </ul>
      )}
    </div>
  );
}
